import React, { useEffect, useState, useCallback } from "react";
import {
  X,
  Loader2,
  FileText,
  Trash2,
  Paperclip,
  Pencil,
  BookOpen,
  Layers,
  ListOrdered,
  Library,
  ImageIcon,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getLessonById, deleteLesson } from "../../../api/elearning/lessons/lessons";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";
import { hasPermission } from "../../../utils/permissionUtils";

function docTypeLabel(type) {
  const t = String(type || "").toLowerCase();
  if (t === "bai_giang" || t === "baigiang") return "Bài giảng (ZIP)";
  if (t === "giaotrinh" || t === "giao_trinh") return "Giáo trình";
  if (t === "bai_tap") return "Bài tập";
  if (t === "de_thi") return "Đề kiểm tra";
  if (t === "video") return "Video";
  return type ? String(type) : "Tài liệu";
}

function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)} MB`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(2)} KB`;
  return `${n} B`;
}

function getLessonOrder(lesson) {
  if (!lesson || typeof lesson !== "object") return null;
  const v = lesson.order;
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function SummaryRow({ label, value, muted = false }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-xs text-slate-400">{label}</span>
      <span
        className={`max-w-[62%] text-right text-sm font-semibold ${
          muted ? "text-slate-400" : "text-slate-700"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function InfoBlock({ icon: Icon, title, children }) {
  return (
    <section className="rounded-[1.75rem] border border-slate-100 bg-white p-5 shadow-sm md:p-6">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-600">
          <Icon size={18} strokeWidth={2.2} />
        </div>
        <h3 className="text-base font-bold text-slate-800">{title}</h3>
      </div>
      {children}
    </section>
  );
}

/**
 * Modal chi tiết bài học (chỉ xem). Sửa → gọi onEdit(lessonId) mở form giống Thêm mới.
 */
const LessonDetailModal = ({
  lessonId,
  isOpen,
  onClose,
  onDeleted,
  onUpdated,
  onEdit,
}) => {
  const [loading, setLoading] = useState(false);
  const [lesson, setLesson] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const canDelete = hasPermission("elearning.lessons", "D");
  const canEdit = hasPermission("elearning.lessons", "U");

  const reloadLesson = useCallback(async () => {
    if (!lessonId) return;
    const data = await getLessonById(lessonId);
    setLesson(data);
  }, [lessonId]);

  useEffect(() => {
    if (!isOpen || !lessonId) {
      setLesson(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getLessonById(lessonId)
      .then((data) => {
        if (!cancelled) setLesson(data);
      })
      .catch((err) => {
        if (!cancelled) {
          showErrorToast(
            err.response?.data?.message ||
              err.message ||
              "Không tải được bài học."
          );
          onClose?.();
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, lessonId, onClose]);

  const documents = Array.isArray(lesson?.documents) ? lesson.documents : [];
  const displayOrder = lesson ? getLessonOrder(lesson) : null;
  const busy = loading || deleteBusy;
  const isActive = lesson?.is_active !== false;

  const handleDelete = async () => {
    if (!lessonId || deleteBusy) return;
    const res = await confirmDelete({
      type: DELETE_ENTITY.lesson,
      name: lesson?.name || lesson?.title,
    });
    if (!res.isConfirmed) return;
    setDeleteBusy(true);
    try {
      await deleteLesson(lessonId);
      showSuccessToast("Đã xóa bài học.");
      onDeleted?.();
      onClose?.();
    } catch (err) {
      showErrorToast(
        err.response?.data?.message || err.message || "Xóa thất bại."
      );
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleEdit = () => {
    if (!lessonId || !canEdit) return;
    onEdit?.(lessonId);
    onClose?.();
  };

  // Giữ API onUpdated cho parent (sau khi sửa xong refresh list)
  useEffect(() => {
    if (!isOpen || !lessonId) return;
    // no-op: parent có thể gọi reload qua onUpdated từ edit modal
  }, [isOpen, lessonId, onUpdated, reloadLesson]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-slate-900/55 backdrop-blur-md"
            onClick={() => !busy && onClose?.()}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="pointer-events-none fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-4"
          >
            <div
              className="pointer-events-auto flex max-h-[min(92vh,860px)] w-full max-w-4xl flex-col overflow-hidden rounded-[1.75rem] border border-slate-100 bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="shrink-0 border-b border-slate-100 bg-gradient-to-r from-red-50 via-white to-white px-6 py-5 md:px-8">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <span className="flex h-12 w-12 shrink-0 overflow-hidden rounded-2xl bg-red-600 text-white shadow-lg shadow-red-200">
                      {lesson?.thumbnail_url ? (
                        <img
                          src={lesson.thumbnail_url}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="flex h-full w-full items-center justify-center">
                          <BookOpen size={22} />
                        </span>
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-red-500">
                        Chi tiết bài học
                      </p>
                      <h2 className="mt-1 text-lg font-black leading-snug tracking-tight text-slate-800 sm:text-xl">
                        {loading
                          ? "Đang tải…"
                          : lesson?.title || "Bài học"}
                      </h2>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${
                            isActive
                              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                              : "border-amber-200 bg-amber-50 text-amber-800"
                          }`}
                        >
                          {isActive ? "Đang hoạt động" : "Chờ cập nhật"}
                        </span>
                        {displayOrder !== null ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
                            <ListOrdered size={12} />
                            Thứ tự {displayOrder}
                          </span>
                        ) : null}
                        <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
                          <Paperclip size={12} />
                          {documents.length} tài liệu
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => !busy && onClose?.()}
                    disabled={busy}
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-slate-400 transition hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                    aria-label="Đóng"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-6 py-6 md:px-8">
                {loading ? (
                  <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
                    <Loader2 className="animate-spin" size={22} />
                    <span className="text-sm font-medium">Đang tải…</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1.4fr)_280px]">
                    <div className="space-y-5">
                      <InfoBlock icon={Layers} title="Liên kết khóa học">
                        <div className="space-y-3">
                          <SummaryRow
                            label="Môn học"
                            value={lesson?.subject_name || "—"}
                            muted={!lesson?.subject_name}
                          />
                          <SummaryRow
                            label="Khóa học"
                            value={lesson?.course_name || "—"}
                            muted={!lesson?.course_name}
                          />
                        </div>
                      </InfoBlock>

                      <InfoBlock icon={FileText} title="Nội dung bài học">
                        <div className="space-y-4">
                          <div>
                            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
                              Mô tả
                            </p>
                            <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                              {lesson?.description?.trim() || (
                                <span className="italic text-slate-400">
                                  Chưa có mô tả
                                </span>
                              )}
                            </p>
                          </div>
                          {lesson?.thumbnail_url ? (
                            <div>
                              <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">
                                Thumbnail
                              </p>
                              <img
                                src={lesson.thumbnail_url}
                                alt=""
                                className="h-28 w-28 rounded-2xl object-cover ring-1 ring-slate-100"
                              />
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-sm text-slate-400">
                              <ImageIcon size={16} />
                              Chưa có ảnh thumbnail
                            </div>
                          )}
                        </div>
                      </InfoBlock>

                      <InfoBlock
                        icon={Library}
                        title={`Tài liệu liên quan (${documents.length})`}
                      >
                        {documents.length === 0 ? (
                          <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-400">
                            Chưa có tài liệu gắn với bài học.
                          </p>
                        ) : (
                          <ul className="space-y-2">
                            {documents.map((doc, idx) => (
                              <li
                                key={doc.id}
                                className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50/70 px-3 py-3"
                              >
                                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white text-xs font-black text-red-700 shadow-sm ring-1 ring-red-100">
                                  {idx + 1}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <p
                                    className="truncate text-sm font-bold text-slate-800"
                                    title={doc.file_name}
                                  >
                                    {doc.file_name || "Tài liệu"}
                                  </p>
                                  <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
                                    <span className="font-semibold uppercase text-slate-400">
                                      {docTypeLabel(doc.type)}
                                    </span>
                                    {doc.file_size != null ? (
                                      <span>{formatBytes(doc.file_size)}</span>
                                    ) : null}
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </InfoBlock>
                    </div>

                    <div className="space-y-5 lg:sticky lg:top-0">
                      <div className="rounded-[1.75rem] border border-slate-200 bg-slate-900 p-5 text-white shadow-xl shadow-slate-900/10">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">
                          Tóm tắt
                        </p>
                        <p className="mt-2 line-clamp-3 text-base font-bold leading-snug">
                          {lesson?.title || "—"}
                        </p>
                        <div className="mt-4 space-y-2.5">
                          <div className="flex justify-between gap-2 text-sm">
                            <span className="text-white/50">Trạng thái</span>
                            <span className="font-semibold">
                              {isActive ? "Hoạt động" : "Chờ cập nhật"}
                            </span>
                          </div>
                          <div className="flex justify-between gap-2 text-sm">
                            <span className="text-white/50">Thứ tự</span>
                            <span className="font-semibold tabular-nums">
                              {displayOrder !== null ? displayOrder : "—"}
                            </span>
                          </div>
                          <div className="flex justify-between gap-2 text-sm">
                            <span className="text-white/50">Tài liệu</span>
                            <span className="font-semibold">
                              {documents.length}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex shrink-0 flex-col-reverse items-stretch justify-between gap-3 border-t border-slate-100 bg-slate-50/80 px-6 py-5 sm:flex-row sm:items-center md:px-8">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onClose?.()}
                  className="rounded-2xl px-6 py-3 text-sm font-bold text-slate-500 transition hover:text-red-600 disabled:opacity-50"
                >
                  Đóng
                </button>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  {canEdit ? (
                    <button
                      type="button"
                      disabled={busy || !lesson}
                      onClick={handleEdit}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-6 py-3 text-sm font-bold text-amber-900 transition hover:bg-amber-100 disabled:opacity-50"
                    >
                      <Pencil size={18} />
                      Sửa bài học
                    </button>
                  ) : null}
                  {canDelete ? (
                    <button
                      type="button"
                      disabled={busy || !lessonId}
                      onClick={handleDelete}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-red-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-red-200 transition hover:bg-red-700 disabled:opacity-50"
                    >
                      {deleteBusy ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Trash2 size={18} />
                      )}
                      Xóa
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default LessonDetailModal;
